import React, { useMemo, useEffect } from "react";
import DashboardMap from "@tripbuilder/cat-origin/middle/dashboardMap";
import Header from "./Header";
import useOriginStore from "@/stores/tripBuilder/useOrigin";
import useTripStore from "@/stores/tripBuilder/useTrip";
import BudgetView from "@tripbuilder/cat-overview/middle/BudgetView";
import TripParticipants from "@tripbuilder/cat-overview/middle/TripParticipants";
import BookingProgressOrigin from "@tripbuilder/cat-overview/middle/bookingProgressCompOrigin";
import WeatherView from "@tripbuilder/cat-overview/middle/WeatherModal";
import ToDoList from "./ToDoList";
import { useWeather } from "@/hooks/useWeather";

export const TripDashboard = () => {
  const tripData = useTripStore((state) => state.tripData);
  const tripTodos = useTripStore((state) => state.tripTodos);
  const { selectedLocation, setSelectedLocation } = useOriginStore();
  const { data: weatherData, isLoading } = useWeather(selectedLocation);
  const totalParticipants = useMemo(() => {
    return tripData?.users?.length || 0;
  }, [tripData]);

  const hasOnlyOrigin = tripData?.locations?.length === 1;

  // Set default selected location to origin if none is selected
  useEffect(() => {
    if (tripData?.locations?.length && !selectedLocation) {
      // Find the origin location (usually the first one)
      const originLocation = tripData.locations[0];
      if (originLocation) {
        setSelectedLocation(originLocation);
      }
    }
  }, [tripData?.locations, selectedLocation, setSelectedLocation]);

  const totalDays = useMemo(() => {
    if (hasOnlyOrigin) return undefined;

    return Math.ceil(
      (new Date(
        tripData!.locations[tripData!.locations.length - 1]!.date!.end_date!
      ).getTime() -
        new Date(tripData!.locations[0]!.date!.start_date!).getTime()) /
        (1000 * 60 * 60 * 24)
    );
  }, [hasOnlyOrigin, tripData]);

  if (!tripData) return null;

  return (
    <div className="flex flex-col w-full h-[calc(100vh-4.4rem)] overflow-hidden">
      {/* Header with Stats */}
      <Header
        tripName="Trip Overview"
        destinations={tripData.locations.length - 1}
        totalDays={totalDays}
        travelers={totalParticipants}
        hasOnlyOrigin={hasOnlyOrigin}
      />

      {/* Map Section */}
      <div className="w-full h-[45vh] 2xl:h-[45vh] mt-3 flex-none">
        <DashboardMap trip={tripData} selectedLocation={selectedLocation} />
      </div>

      {/* Location Overview */}
      {selectedLocation && (
        <div className="w-full h-[50vh] 2xl:h-[25vh] flex-1 rounded-xl overflow-hidden bg-white">
          {/* Location Details Grid */}
          <div className="py-2 h-full ">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 h-full">
              <ToDoList todos={tripTodos} tripId={tripData.id} />

              {/* Trip Participants */}
              <div className="md:col-span-1 bg-white rounded-xl shadow-sm overflow-hidden border border-neutral-200 flex flex-col h-full">
                <div className="px-4 py-3 border-b border-neutral-100 bg-white">
                  <h3 className="text-sm 2xl:text-base font-medium text-neutral-700">
                    Booking Status
                  </h3>
                </div>
                <div className="flex flex-col flex-grow space-y-1 bg-neutral-50">
                  <div className="flex-grow p-2">
                    <BookingProgressOrigin
                      locationId={selectedLocation?.id || ""}
                      variant="expanded"
                    />
                  </div>
                </div>
              </div>

              {/* Booking Progress */}
              <div className="md:col-span-1 rounded-xl shadow-sm overflow-hidden border border-neutral-200 bg-neutral-50 flex flex-col h-full">
                <div className="px-4 py-3 border-b border-neutral-100 bg-white">
                  <h3 className="text-sm 2xl:text-base font-medium text-neutral-700">
                    Participants & Budget
                  </h3>
                </div>
                <div className="flex-grow p-2">
                  <div className="flex flex-col h-full space-y-0.5">
                    <div>
                      <TripParticipants
                        users={tripData.users}
                        locationId={selectedLocation?.id || ""}
                      />
                    </div>

                    <div className="pt-2 flex flex-row space-x-1 flex-grow h-full">
                      <BudgetView locationId={selectedLocation?.id || ""} />
                      <WeatherView
                        locationId={selectedLocation?.id || ""}
                        name={selectedLocation?.name || ""}
                        weatherData={weatherData || null}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripDashboard;
